import React, {useMemo, useState} from 'react';
import {StyleSheet, TouchableOpacity, View} from 'react-native';
import Box from '@/Atoms/Box';
import Card from '@/Atoms/Card';
import CCText from '@/Atoms/CCText';
import Ionicons from 'react-native-vector-icons/Ionicons';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import {
  ExplorerConfig,
  ExplorerData,
  ExplorerError,
  ExplorerGame,
  ExplorerSpeed,
  EXPLORER_RATINGS,
  EXPLORER_SPEEDS,
} from '@/Hooks/OpeningExplorer/useOpeningExplorer';
import {formatGameCount, wdlPercents} from '../../utils/explorerUtils';

/**
 * Presentational opening-explorer panel for the Analysis tab, modeled on
 * lichess's own: database switcher (Masters / Lichess with speed+rating
 * filters), per-move win/draw/loss bars, and a Top/Recent games list. All
 * data comes in via props (see useOpeningExplorer); the component owns only
 * collapse/filter-visibility state.
 */

interface OpeningExplorerProps {
  data: ExplorerData | undefined;
  isLoading?: boolean;
  isError?: boolean;
  /** The raw error from useOpeningExplorer — used to show a precise message. */
  error?: unknown;
  config: ExplorerConfig;
  onConfigChange: (config: ExplorerConfig) => void;
  onMoveClick: (san: string) => void;
  isMoveInTree: (san: string) => boolean;
  /** Normal-analysis chapters: clicking a move that isn't in the tree yet
   * plays it onto the board (adding a branch), like lichess. Elsewhere
   * those rows stay disabled so a lesson/practice line can't be sidestepped. */
  canAddNewMoves?: boolean;
  onGamePress?: (gameId: string) => void;
}

function resultChip(game: ExplorerGame) {
  if (game.winner === 'white') {
    return {
      label: '1-0',
      bg: CCColors.White,
      fg: CCColors.TextColor.Dark,
      border: updateColorWithOpacity(CCColors.TextColor.Dark, 0.35),
    };
  }
  if (game.winner === 'black') {
    return {
      label: '0-1',
      bg: CCColors.TextColor.Dark,
      fg: CCColors.White,
      border: CCColors.TextColor.Dark,
    };
  }
  return {
    label: '½-½',
    bg: CCColors.Workbook.Beige,
    fg: CCColors.TextColor.Primary,
    border: CCColors.Workbook.BeigeDark,
  };
}

const OpeningExplorer: React.FC<OpeningExplorerProps> = ({
  data,
  isLoading,
  isError,
  error,
  config,
  onConfigChange,
  onMoveClick,
  isMoveInTree,
  canAddNewMoves,
  onGamePress,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const rows = useMemo(() => {
    return [...(data?.moves || [])]
      .map(m => ({...m, total: m.white + m.draws + m.black}))
      .filter(m => m.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [data?.moves]);

  const games: ExplorerGame[] = data?.topGames?.length
    ? data.topGames
    : data?.recentGames || [];
  const gamesLabel = data?.topGames?.length ? 'Top games' : 'Recent games';

  const toggleSpeed = (speed: ExplorerSpeed) => {
    const has = config.speeds.includes(speed);
    const speeds = has
      ? config.speeds.filter(s => s !== speed)
      : [...config.speeds, speed];
    if (speeds.length) {
      onConfigChange({...config, speeds});
    }
  };

  const toggleRating = (rating: number) => {
    const has = config.ratings.includes(rating);
    const ratings = has
      ? config.ratings.filter(r => r !== rating)
      : [...config.ratings, rating];
    if (ratings.length) {
      onConfigChange({...config, ratings});
    }
  };

  // Whether there's anything worth collapsing. An empty result (no moves,
  // no games, no error, done loading) has nothing to hide, so the section
  // stays put as a fixed "no data" message instead of a collapsible panel —
  // matches lichess, which always keeps the explorer panel mounted rather
  // than making it vanish for unexplored positions.
  const hasContent = isLoading || isError || !!rows.length || !!games.length;
  const isCollapsible = hasContent;

  return (
    <Card style={styles.container}>
      <TouchableOpacity
        style={styles.headerRow}
        disabled={!isCollapsible}
        onPress={() => isCollapsible && setCollapsed(prev => !prev)}>
        <CCText
          fontSize={13}
          fontWeight="bold"
          color={CCColors.TextColor.Primary}>
          Opening Explorer
        </CCText>
        {isCollapsible && (
          <Ionicons
            name={collapsed ? 'chevron-down' : 'chevron-up'}
            size={16}
            color={CCColors.Workbook.Brown}
          />
        )}
      </TouchableOpacity>

      {!collapsed && (
        <Box style={{marginTop: 8}}>
          {/* Database switcher */}
          <Box style={styles.dbRow}>
            <Box style={styles.dbPills}>
              {(['masters', 'lichess'] as const).map(db => (
                <TouchableOpacity
                  key={db}
                  testID={`explorer-db-${db}`}
                  onPress={() => onConfigChange({...config, db})}
                  style={[
                    styles.dbPill,
                    config.db === db && styles.dbPillActive,
                  ]}>
                  <CCText
                    fontSize={11}
                    fontWeight="semibold"
                    color={
                      config.db === db
                        ? CCColors.TextColor.Dark
                        : CCColors.Workbook.Brown
                    }>
                    {db === 'masters' ? 'Masters' : 'Lichess'}
                  </CCText>
                </TouchableOpacity>
              ))}
            </Box>
            {config.db === 'lichess' && (
              <TouchableOpacity
                testID="explorer-filter-toggle"
                onPress={() => setShowFilters(prev => !prev)}
                style={styles.filterToggle}>
                <Ionicons
                  name="options-outline"
                  size={13}
                  color={CCColors.Workbook.Brown}
                />
                <CCText
                  fontSize={10.5}
                  color={CCColors.Workbook.Brown}
                  fontWeight="semibold">
                  Filters
                </CCText>
              </TouchableOpacity>
            )}
          </Box>

          {config.db === 'lichess' && showFilters && (
            <Box style={styles.filters}>
              <Box style={styles.chipRow}>
                {EXPLORER_SPEEDS.map(speed => {
                  const on = config.speeds.includes(speed);
                  return (
                    <TouchableOpacity
                      key={speed}
                      onPress={() => toggleSpeed(speed)}
                      style={[styles.chip, on && styles.chipOn]}>
                      <CCText
                        fontSize={10}
                        color={
                          on
                            ? CCColors.TextColor.Dark
                            : CCColors.Workbook.BrownGrey
                        }
                        fontWeight={on ? 'semibold' : 'regular'}>
                        {speed}
                      </CCText>
                    </TouchableOpacity>
                  );
                })}
              </Box>
              <Box style={styles.chipRow}>
                {EXPLORER_RATINGS.map(rating => {
                  const on = config.ratings.includes(rating);
                  return (
                    <TouchableOpacity
                      key={rating}
                      onPress={() => toggleRating(rating)}
                      style={[styles.chip, on && styles.chipOn]}>
                      <CCText
                        fontSize={10}
                        color={
                          on
                            ? CCColors.TextColor.Dark
                            : CCColors.Workbook.BrownGrey
                        }
                        fontWeight={on ? 'semibold' : 'regular'}>
                        {rating}
                      </CCText>
                    </TouchableOpacity>
                  );
                })}
              </Box>
            </Box>
          )}

          {!!data?.opening?.name && (
            <CCText
              fontSize={11}
              color={CCColors.Workbook.Brown}
              style={{marginBottom: 6}}>
              {data.opening.eco ? `${data.opening.eco} · ` : ''}
              {data.opening.name}
            </CCText>
          )}

          {isError ? (
            <CCText
              fontSize={11}
              color={CCColors.Workbook.BrownGrey}
              style={{paddingVertical: 8}}>
              {error instanceof ExplorerError && error.kind === 'auth'
                ? 'Opening explorer requires a Lichess token — contact support.'
                : error instanceof ExplorerError && error.kind === 'rate-limit'
                ? 'Opening database is busy — retrying automatically.'
                : 'Opening database unavailable — retrying automatically.'}
            </CCText>
          ) : isLoading ? (
            <CCText
              fontSize={11}
              color={CCColors.Workbook.BrownGrey}
              style={{paddingVertical: 8}}>
              Loading opening database…
            </CCText>
          ) : !rows.length && !games.length ? (
            <CCText
              fontSize={11}
              color={CCColors.Workbook.BrownGrey}
              style={{paddingVertical: 8}}>
              No games found in the opening database for this position.
            </CCText>
          ) : (
            <>
              {/* Column header */}
              <Box style={styles.tableHead}>
                <CCText
                  fontSize={10.5}
                  color={CCColors.Workbook.BrownGrey}
                  style={styles.colMove}>
                  Move
                </CCText>
                <CCText
                  fontSize={10.5}
                  color={CCColors.Workbook.BrownGrey}
                  style={styles.colGames}>
                  Games
                </CCText>
                <CCText
                  fontSize={10.5}
                  color={CCColors.Workbook.BrownGrey}
                  style={styles.colBar}>
                  White / Draw / Black
                </CCText>
              </Box>

              {rows.map(row => {
                const inTree = isMoveInTree(row.san);
                const clickable = inTree || !!canAddNewMoves;
                const pct = wdlPercents(row.white, row.draws, row.black);
                return (
                  <TouchableOpacity
                    key={row.uci || row.san}
                    testID={`explorer-move-${row.san}`}
                    style={[styles.moveRow, !clickable && {opacity: 0.45}]}
                    onPress={() => clickable && onMoveClick(row.san)}
                    disabled={!clickable}>
                    <CCText
                      fontSize={12.5}
                      color={inTree ? CCColors.TextColor.Dark : CCColors.TextColor.Primary}
                      fontWeight={inTree ? 'bold' : 'semibold'}
                      style={styles.colMove}>
                      {row.san}
                    </CCText>
                    <CCText
                      fontSize={11}
                      color={CCColors.Workbook.Brown}
                      style={[
                        styles.colGames,
                        {fontVariant: ['tabular-nums']} as any,
                      ]}>
                      {formatGameCount(row.total)}
                    </CCText>
                    <View style={styles.colBar}>
                      <View style={styles.wdlBar}>
                        {pct.white > 0 && (
                          <View style={[styles.wdlWhite, {flex: pct.white}]}>
                            {pct.white >= 20 && (
                              <CCText style={styles.wdlTextDark}>
                                {pct.white}%
                              </CCText>
                            )}
                          </View>
                        )}
                        {pct.draws > 0 && (
                          <View style={[styles.wdlDraw, {flex: pct.draws}]}>
                            {pct.draws >= 20 && (
                              <CCText style={styles.wdlTextDark}>
                                {pct.draws}%
                              </CCText>
                            )}
                          </View>
                        )}
                        {pct.black > 0 && (
                          <View style={[styles.wdlBlack, {flex: pct.black}]}>
                            {pct.black >= 20 && (
                              <CCText style={styles.wdlTextLight}>
                                {pct.black}%
                              </CCText>
                            )}
                          </View>
                        )}
                      </View>
                    </View>
                  </TouchableOpacity>
                );
              })}

              {games.length > 0 && (
                <Box style={{marginTop: 10}}>
                  <CCText
                    fontSize={10.5}
                    fontWeight="bold"
                    color={CCColors.Workbook.BrownGrey}
                    style={styles.gamesLabel}>
                    {gamesLabel}
                  </CCText>
                  {games.map(game => {
                    const chip = resultChip(game);
                    return (
                      <TouchableOpacity
                        key={game.id}
                        style={styles.gameRow}
                        onPress={() => onGamePress?.(game.id)}>
                        <View
                          style={[
                            styles.resultChip,
                            {
                              backgroundColor: chip.bg,
                              borderColor: chip.border,
                            },
                          ]}>
                          <CCText
                            fontSize={9.5}
                            fontWeight="bold"
                            color={chip.fg}>
                            {chip.label}
                          </CCText>
                        </View>
                        <Box style={{flex: 1, marginLeft: 8}}>
                          <CCText
                            fontSize={11.5}
                            color={CCColors.TextColor.Dark}
                            numberOfLines={1}>
                            {game.white.name}
                            {game.white.rating ? ` (${game.white.rating})` : ''}
                            {'  ·  '}
                            {game.black.name}
                            {game.black.rating ? ` (${game.black.rating})` : ''}
                          </CCText>
                        </Box>
                        {!!game.year && (
                          <CCText fontSize={10.5} color={CCColors.Workbook.BrownGrey}>
                            {game.year}
                          </CCText>
                        )}
                      </TouchableOpacity>
                    );
                  })}
                </Box>
              )}
            </>
          )}

          <CCText fontSize={9.5} color={CCColors.Workbook.BeigeBorder} style={{marginTop: 8}}>
            Data: Lichess opening explorer
          </CCText>
        </Box>
      )}
    </Card>
  );
};

const styles = StyleSheet.create({
  container: {},
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dbRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  dbPills: {
    flexDirection: 'row',
    backgroundColor: CCColors.Primary.Light,
    borderRadius: 8,
    padding: 2,
    gap: 2,
  },
  dbPill: {
    paddingVertical: 5,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  dbPillActive: {
    backgroundColor: CCColors.Primary.Default,
  },
  filterToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.15),
  },
  filters: {
    gap: 6,
    marginBottom: 8,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 5,
  },
  chip: {
    paddingVertical: 3,
    paddingHorizontal: 9,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.15),
    backgroundColor: CCColors.White,
  },
  chipOn: {
    backgroundColor: CCColors.LightBrownShade,
    borderColor: CCColors.Progress.Bar,
  },
  tableHead: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
  },
  colMove: {width: 64},
  colGames: {width: 52, textAlign: 'right', paddingRight: 10},
  colBar: {flex: 1},
  moveRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 5,
    borderTopWidth: 1,
    borderTopColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.06),
  },
  wdlBar: {
    flexDirection: 'row',
    height: 16,
    borderRadius: 4,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.12),
  },
  wdlWhite: {
    backgroundColor: CCColors.White,
    alignItems: 'center',
    justifyContent: 'center',
  },
  wdlDraw: {
    backgroundColor: CCColors.Workbook.BeigeDark,
    alignItems: 'center',
    justifyContent: 'center',
  },
  wdlBlack: {
    backgroundColor: CCColors.TextColor.Primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  wdlTextDark: {fontSize: 9, color: CCColors.TextColor.Primary},
  wdlTextLight: {fontSize: 9, color: CCColors.Workbook.Cream},
  gamesLabel: {
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  gameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 5,
    borderTopWidth: 1,
    borderTopColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.06),
  },
  resultChip: {
    width: 34,
    alignItems: 'center',
    paddingVertical: 2,
    borderRadius: 4,
    borderWidth: 1,
  },
});

export default OpeningExplorer;
